import React, { useEffect, useRef, useState } from "react";
import { motion, animate, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { trackInitiateCheckout } from "@/lib/fbpixel";
import { usePricing } from "@/lib/pricing";

/* ---------- Scroll-triggered count-up number ---------- */
export const CountUp = ({ to, format, duration = 1.8, className = "", testId }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!inView) return;
    const controls = animate(0, to, {
      duration,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (v) => setVal(v),
    });
    return () => controls.stop();
  }, [inView, to, duration, setVal]);
  return (
    <span ref={ref} data-testid={testId} className={className}>
      {format ? format(val) : Math.round(val)}
    </span>
  );
};

/* ---------- CTA Button ---------- */
export const CTAButton = ({ children, variant = "cognac", testId, className = "", showArrow = true }) => {
  const { checkoutUrl } = usePricing();
  const styles = {
    cognac:
      "bg-cognac-2 text-gold hover:bg-espresso shadow-[0_10px_30px_-12px_rgba(62,39,35,0.6)]",
    espresso:
      "bg-espresso text-gold hover:bg-cognac-2 shadow-[0_10px_30px_-12px_rgba(62,39,35,0.6)]",
    gold: "bg-gold text-espresso hover:bg-cognac-2 hover:text-gold",
  };
  return (
    <a
      href={checkoutUrl}
      target="_blank"
      rel="noopener noreferrer"
      onClick={trackInitiateCheckout}
      data-testid={testId}
      className={`group relative inline-flex items-center gap-3 rounded-full px-9 py-4 font-heading text-sm font-600 uppercase tracking-[0.18em] transition-[background-color,color,transform] duration-300 hover:-translate-y-0.5 ${styles[variant]} ${className}`}
      style={{ fontWeight: 600 }}
    >
      <span>{children}</span>
      {showArrow && (
        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
      )}
    </a>
  );
};

/* ---------- Fanned card-deck icon (recurring motif) ---------- */
export const CardDeck = ({ className = "" }) => (
  <svg viewBox="0 0 26 22" fill="none" aria-hidden="true" className={className}>
    <rect x="3" y="4" width="9" height="14" rx="1.6" stroke="currentColor" strokeWidth="2" opacity="0.55" transform="rotate(-12 7.5 11)" />
    <rect x="8.5" y="3.5" width="9" height="14" rx="1.6" stroke="currentColor" strokeWidth="2" opacity="0.8" />
    <rect x="14" y="4" width="9" height="14" rx="1.6" stroke="currentColor" strokeWidth="2" transform="rotate(12 18.5 11)" />
  </svg>
);

/* ---------- Section Label (small-caps anchor) ---------- */
export const SectionLabel = ({ children, color = "cognac", testId }) => {
  const colors = {
    cognac: "text-cognac-2",
    gold: "text-gold/80",
    espresso: "text-espresso",
    pine: "text-pine",
  };
  return (
    <div
      data-testid={testId}
      className={`mb-8 flex items-center gap-4 font-heading text-xs font-700 uppercase tracking-[0.32em] ${colors[color]}`}
      style={{ fontWeight: 700 }}
    >
      <CardDeck className="h-5 w-7 shrink-0" />
      <span className="h-px w-10 bg-current opacity-60" />
      {children}
    </div>
  );
};

/* ---------- Scroll reveal wrapper ---------- */
export const Reveal = ({ children, delay = 0, y = 28, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.8, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ---------- Editorial marquee ---------- */
export const Marquee = ({ dark = true }) => {
  const word = "UNVERSCHÄMT ERFOLGREICH";
  const items = Array.from({ length: 8 }, (_, i) => `rep-${i}`);
  return (
    <div
      data-testid="marquee"
      className={`grain relative overflow-hidden border-y py-6 ${
        dark ? "border-cognac/40 bg-espresso" : "border-espresso/20 bg-gold"
      }`}
    >
      <div className="marquee-track">
        {["group-a", "group-b"].map((g) => (
          <div key={g} className="flex shrink-0 items-center">
            {items.map((rep) => (
              <span key={`${g}-${rep}`} className="flex items-center">
                <span
                  className={`font-heading text-2xl font-600 uppercase tracking-[0.14em] md:text-4xl ${
                    dark ? "text-gold" : "text-espresso"
                  }`}
                  style={{ fontWeight: 600 }}
                >
                  {word}
                </span>
                <span
                  className={`mx-8 text-2xl md:text-4xl ${
                    dark ? "text-cognac-light" : "text-cognac-2"
                  }`}
                >
                  🫦
                </span>
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};
